<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { Building } from '@lucide/vue';
import { impostazioniAziendaSchema, type ImpostazioniAzienda } from '@evolfin/shared';
import { EvolfinApiError } from '@/api/client';
import { useAvvisi } from '@/composables/useAvvisi';
import { settingsKeys } from '../query-keys';
import CampoTesto from '@/components/ui/CampoTesto.vue';
import CampoIndirizzo, { type IndirizzoForm } from '@/components/ui/CampoIndirizzo.vue';
import PulsanteAzione from '@/components/ui/PulsanteAzione.vue';
import StatoCaricamento from '@/components/ui/StatoCaricamento.vue';
import NavImpostazioni from '../components/NavImpostazioni.vue';
import { leggiImpostazioniAzienda, salvaImpostazioniAzienda } from '../api';

// Pagina di sola gestione: senza il privilegio il router la nasconde.
definePage({ meta: { titolo: 'Impostazioni', privilegi: ['can_manage_impostazioni'] } });

const avvisi = useAvvisi();

/**
 * Dati dell'azienda che usa il gestionale: è ciò che intesta i documenti
 * generati, quindi finché non c'è il verbale di fase non si può produrre.
 * L'endpoint risponde 404 se non è mai stata configurata: qui è uno stato
 * normale (form vuoto), non un errore da mostrare.
 */
const { data, isLoading, refetch } = useQuery({
  queryKey: settingsKeys.company(),
  queryFn: async (): Promise<ImpostazioniAzienda | null> => {
    try {
      return await leggiImpostazioniAzienda();
    } catch (errore) {
      const mancante =
        errore instanceof EvolfinApiError &&
        errore.dettagli.code === 'IMPOSTAZIONI_NON_CONFIGURATE';
      if (mancante) return null;
      throw errore;
    }
  },
  retry: false,
});

function indirizzoVuoto(): IndirizzoForm {
  return { via: '', civico: '', cap: '', comune: '', provincia: '', regione: '' };
}

const form = reactive({
  ragioneSociale: '',
  codiceFiscale: '',
  partitaIva: '',
  email: '',
  pec: '',
  telefono: '',
  sitoWeb: '',
  sedeLegale: indirizzoVuoto(),
});

watch(
  data,
  (a) => {
    if (!a) return;
    form.ragioneSociale = a.ragioneSociale;
    form.codiceFiscale = a.codiceFiscale ?? '';
    form.partitaIva = a.partitaIva ?? '';
    form.email = a.email ?? '';
    form.pec = a.pec ?? '';
    form.telefono = a.telefono ?? '';
    form.sitoWeb = a.sitoWeb ?? '';
    form.sedeLegale = {
      via: a.sedeLegale.via,
      civico: a.sedeLegale.civico ?? '',
      cap: a.sedeLegale.cap,
      comune: a.sedeLegale.comune,
      provincia: a.sedeLegale.provincia,
      regione: a.sedeLegale.regione,
    };
  },
  { immediate: true },
);

const erroriCampo = ref<Record<string, string[]>>({});
const erroreGenerale = ref<string | null>(null);
const salvataggio = ref(false);
const primoErrore = (k: string) => erroriCampo.value[k]?.[0];
const mai = computed(() => !isLoading.value && data.value == null);

async function salva(): Promise<void> {
  erroriCampo.value = {};
  erroreGenerale.value = null;

  const esito = impostazioniAziendaSchema.safeParse({
    ragioneSociale: form.ragioneSociale,
    codiceFiscale: form.codiceFiscale || undefined,
    partitaIva: form.partitaIva || undefined,
    email: form.email || undefined,
    pec: form.pec || undefined,
    telefono: form.telefono || undefined,
    sitoWeb: form.sitoWeb || undefined,
    sedeLegale: { ...form.sedeLegale, civico: form.sedeLegale.civico || undefined },
  });
  if (!esito.success) {
    for (const i of esito.error.issues) {
      (erroriCampo.value[i.path.map(String).join('.')] ??= []).push(i.message);
    }
    return;
  }

  salvataggio.value = true;
  try {
    await salvaImpostazioniAzienda(esito.data);
    avvisi.successo('Impostazioni salvate');
    await refetch();
  } catch (errore) {
    erroreGenerale.value =
      errore instanceof EvolfinApiError ? errore.message : 'Operazione non riuscita';
  } finally {
    salvataggio.value = false;
  }
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <header class="flex items-start gap-3">
      <span
        class="grid size-12 shrink-0 place-items-center rounded-lg bg-[var(--surface-accent-soft)] text-[var(--color-primary)]"
        aria-hidden="true"
      >
        <Building :size="22" :stroke-width="1.8" />
      </span>
      <div>
        <p class="text-xs font-semibold tracking-[0.08em] text-ink-muted uppercase">Impostazioni</p>
        <h1 class="mt-1 font-display text-[26px] leading-tight font-semibold">Dati dell'azienda</h1>
        <p class="mt-0.5 text-[13px] text-ink-secondary">
          Intestano i documenti generati dal gestionale, a partire dal verbale di fase.
        </p>
      </div>
    </header>

    <NavImpostazioni />

    <StatoCaricamento v-if="isLoading" />

    <form v-else class="flex flex-col gap-5" novalidate @submit.prevent="salva">
      <p
        v-if="mai"
        class="rounded-md border border-line bg-[var(--surface-accent-soft)] px-4 py-3 text-[13px] text-ink-secondary"
      >
        Non ancora configurati: finché mancano non è possibile generare un verbale di fase.
      </p>

      <section class="card flex flex-col gap-4 p-5">
        <h2 class="text-[11px] font-semibold tracking-[0.07em] text-ink-muted uppercase">
          Anagrafica
        </h2>
        <CampoTesto
          v-model="form.ragioneSociale"
          etichetta="Ragione sociale"
          richiesto
          :errore="primoErrore('ragioneSociale')"
        />
        <div class="grid gap-4 sm:grid-cols-2">
          <CampoTesto
            v-model="form.partitaIva"
            etichetta="Partita IVA"
            tabulare
            :errore="primoErrore('partitaIva')"
          />
          <CampoTesto
            v-model="form.codiceFiscale"
            etichetta="Codice fiscale"
            tabulare
            :errore="primoErrore('codiceFiscale')"
            nota="Almeno uno tra partita IVA e codice fiscale."
          />
        </div>
      </section>

      <section class="card flex flex-col gap-4 p-5">
        <h2 class="text-[11px] font-semibold tracking-[0.07em] text-ink-muted uppercase">
          Sede legale
        </h2>
        <CampoIndirizzo v-model="form.sedeLegale" prefisso="sedeLegale" :errori="erroriCampo" />
      </section>

      <section class="card flex flex-col gap-4 p-5">
        <h2 class="text-[11px] font-semibold tracking-[0.07em] text-ink-muted uppercase">
          Recapiti
        </h2>
        <div class="grid gap-4 sm:grid-cols-2">
          <CampoTesto
            v-model="form.telefono"
            etichetta="Telefono"
            :errore="primoErrore('telefono')"
          />
          <CampoTesto
            v-model="form.email"
            etichetta="Email"
            tipo="email"
            :errore="primoErrore('email')"
          />
          <CampoTesto
            v-model="form.pec"
            etichetta="PEC"
            tipo="email"
            :errore="primoErrore('pec')"
          />
          <CampoTesto
            v-model="form.sitoWeb"
            etichetta="Sito web"
            :errore="primoErrore('sitoWeb')"
            nota="Compreso di https://"
          />
        </div>
      </section>

      <p v-if="erroreGenerale" class="text-[12.5px] text-[var(--color-danger-text)]">
        {{ erroreGenerale }}
      </p>

      <div class="flex justify-end">
        <PulsanteAzione tipo="submit" :caricamento="salvataggio">Salva</PulsanteAzione>
      </div>
    </form>
  </div>
</template>
